import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";

interface Props {
    label?: string;
    disabled?: boolean;
    placeholder?: string;
    options: SelectOption[];
    defaultValue?: string[] | string;
    onChange?: Props['multiselect'] extends true ? (options: SelectOption[]) => void : (option: SelectOption) => void;
    multiselect?: boolean;
}

export type SelectOption = {
    value: string;
    label: string;
    renderLabel?: (option: SelectOption) => React.ReactNode;
    renderSelected?: (option: SelectOption) => React.ReactNode;
};

function renderOption(option?: SelectOption) {
    if (!option) {
        return null;
    }

    if (option.renderLabel) {
        return option.renderLabel(option);
    }

    const label = option.label || option.value;

    return (
        <span className="overflow-hidden text-ellipsis">{label}</span>
    );
}

function renderSelected(option?: SelectOption) {
    if (!option) {
        return null;
    }

    if (option.renderSelected) {
        return option.renderSelected(option);
    }

    const label = option.label || option.value;

    return (
        <span className="overflow-hidden text-ellipsis">{label}</span>
    );
}

export function Select(props: Props) {
    const { disabled, label, placeholder, defaultValue, options, onChange, multiselect = false } = props;

    const defaultSelected = useMemo(() => {
        if (multiselect) {
            return defaultValue ? options.filter(option => defaultValue.includes(option.value)) : [];
        } else {
            return defaultValue ? options.find(option => option.value === defaultValue) : undefined;
        }
    }, [defaultValue, multiselect, options]);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<SelectOption[] | SelectOption | undefined>(defaultSelected);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isDropdownOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isDropdownOpen]);

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const handleOptionClick = (option: SelectOption) => {
        if (multiselect) {
            const selectedOptions = selected as SelectOption[];
            const isSelected = selectedOptions.some(selectedOption => selectedOption.value === option.value);

            const newSelected = isSelected
                ? selectedOptions.filter(selectedOption => selectedOption.value !== option.value)
                : [...selectedOptions, option];

            setSelected(newSelected);
            (onChange as unknown as (options: SelectOption[]) => void)?.(newSelected);
        } else {
            setSelected(option);
            setIsDropdownOpen(false);
            (onChange as unknown as (option: SelectOption) => void)?.(option);
        }
    };

    function renderPlaceholder() {
        return (
            <span className="text-gray-500 whitespace-nowrap">{placeholder ?? 'Select option'}</span>
        );
    }

    function renderValue() {
        if (multiselect) {
            if (!selected || (selected as SelectOption[]).length === 0) {
                return renderPlaceholder();
            }

            return (
                <div className="flex flex-wrap gap-2">
                    {(selected as SelectOption[]).map(option => (
                        <div
                            key={option.value}
                            className="flex items-center space-x-1 bg-indigo-100 px-2 py-1 rounded"
                        >
                            {renderSelected(option)}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();

                                    const newSelected = (selected as SelectOption[]).filter(
                                        selectedOption => selectedOption.value !== option.value
                                    );
                                    setSelected(newSelected);
                                    (onChange as unknown as (options: SelectOption[]) => void)?.(newSelected);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            );
        }

        return selected ? (
            <div className="flex items-center space-x-3 whitespace-nowrap overflow-hidden">
                {renderSelected(selected as SelectOption)}
            </div>
        ) : renderPlaceholder();
    }

    function isOptionSelected(option: SelectOption) {
        // on initial load could be undefined for multiselect
        if (!selected) {
            return false;
        }

        if (multiselect) {
            return (selected as SelectOption[]).some(selectedOption => selectedOption.value === option.value);
        }

        return (selected as SelectOption).value === option.value;
    }

    function renderSelectedSign(option: SelectOption) {
        if (multiselect) {
            return (selected as SelectOption[]).some(selectedOption => selectedOption.value === option.value) ? (
                <span className="text-indigo-600">✔</span>
            ) : null;
        }

        return null;
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                </label>
            )}

            <div className="relative">
                <button
                    disabled={disabled}
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full ps-4 pe-8 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                    title={selected ? (multiselect ? (selected as SelectOption[]).map(option => option.label).join(', ') : (selected as SelectOption).label) : placeholder}
                >
                    {renderValue()}
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                    </span>
                </button>
            </div>

            {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                    </div>
                    <div className="overflow-auto max-h-48">
                        {filteredOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => handleOptionClick(option)}
                                className={`w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-indigo-50 ${
                                    isOptionSelected(option) ? 'bg-indigo-100' : ''
                                }`}
                            >
                                {renderOption(option)}
                                {renderSelectedSign(option)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
